import React from "react";
import Link from "gatsby-link";
import styled, { ThemeProvider } from "styled-components";
import { shade, rgba } from "polished";
import theme from "../theme.js";
import invertTheme from "../invertTheme.js";
import { Box, Flex } from "rebass";
import { Button, H3, H4, Text, OverlayImage, Tag, TagList } from "components";
import moment from "moment";

const Action = styled(Box)`
  text-decoration: none;
  cursor: pointer;
  opacity: 0;
  animation: update-card-fade-in 1s cubic-bezier(0.23, 1, 0.32, 1) forwards;
  will-change: opacity;

  @keyframes update-card-fade-in {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  &:hover {
    .update-card__tile {
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
      transform: translateY(-4px);
    }
    .update-card__title {
      box-shadow: inset 0 -1px 0 ${(props) => props.theme.colors.grey[0]};
    }
  }
  &:active {
    .update-card__tile {
      box-shadow: none;
      transform: translateY(-2px);
    }
    .update-card__title {
      box-shadow: inset 0 -1px 0 ${(props) => props.theme.colors.grey[1]};
    }
  }

  * {
    text-decoration: none;

    &::selection {
      color: ${(props) => props.theme.colors.grey[2]};
      background-color: ${(props) =>
        props.theme.colors[props.cardColor] &&
        shade(0.9375, props.theme.colors[props.cardColor])};
    }
  }
`;

Action.defaultProps = {
  is: Link,
};

const ImageWrapper = styled(Box)`
  position: relative;
  width: 100%;
  padding-bottom: ${(2 / 3) * 100 + "%"};
  /* background-color: ${(props) => props.theme.colors[props.cardColor]};
  background-color: ${(props) => props.theme.colors.grey[1]}; */
  transition: transform 500ms cubic-bezier(0.23, 1, 0.32, 1),
    box-shadow 500ms cubic-bezier(0.23, 1, 0.32, 1);
  will-change: transform;
  user-select: none;
  &:not(:lang(ar)) {
    &:before,
    &:after {
      right: 0;
    }
    &:after {
      background: ${(props) =>
        props.noColor
          ? ""
          : props.theme.colors.grey[1] &&
            "linear-gradient(to right, " +
              rgba(props.theme.colors.grey[1], 0.75) +
              ", " +
              rgba(props.theme.colors.grey[1], 0) +
              ")"};
    }
  }
  &:lang(ar) {
    &:before,
    &:after {
      left: 0;
    }
    &:after {
      background: ${(props) =>
        props.noColor
          ? ""
          : props.theme.colors.grey[1] &&
            "linear-gradient(to left, " +
              rgba(props.theme.colors.grey[1], 0.75) +
              ", " +
              rgba(props.theme.colors.grey[1], 0) +
              ")"};
    }
  }
`;

const Title = styled(H3)`
  margin-top: 8px;
  padding-bottom: 8px;
  font-size: 18px;

  > span {
    box-shadow: inset 0 -1px 0 transparent;
    transition: box-shadow 500ms cubic-bezier(0.23, 1, 0.32, 1);
    will-change: box-shadow;
  }
`;

const Layout = styled(Flex)`
  flex: 1;
`;

const Metadata = styled(H4)``;

const determineColor = function (region) {
  let color = "violet";
  if (
    region === "update" ||
    region === "institution" ||
    region === null ||
    region === undefined
  ) {
    color = "ash";
  }
  if (region === "middle-east-and-north-africa") {
    color = "orange";
  }
  if (region === "africa") {
    color = "green";
  }
  if (region === "latin-america") {
    color = "blue";
  }
  if (region === "asia") {
    color = "pink";
  }
  if (region === "europe--central-asia") {
    color = "yellow";
  }
  return color;
};

const determineLabel = function (region) {
  let label = "BIC";
  if (region === "middle-east-and-north-africa") {
    label = "Middle East and North Africa";
  }
  if (region === "oriente-medio-y-norte-de-africa") {
    label = "Oriente Medio y Norte de África";
  }
  if (region === "الشرق-الاوسط-وشمات-افريقيا") {
    label = "الشرق الأوسط وشمات أفريقيا";
  }
  if (region === "africa") {
    label = "Africa";
  }
  if (region === "africa1") {
    label = "África";
  }
  if (region === "افريقيا") {
    label = "أفريقيا";
  }
  if (region === "latin-america") {
    label = "Latin America";
  }
  if (region === "america-latina-y-el-caribe") {
    label = "América Latina y el Caribe";
  }
  if (region === "امريكا-اللاتينية") {
    label = "أمريكا  اللاتينية";
  }
  if (region === "asia") {
    label = "Asia";
  }
  if (region === "asia1") {
    label = "Asia";
  }
  if (region === "اسيا") {
    label = "آسيا";
  }
  if (region === "europe--central-asia") {
    label = "Europe & Central Asia";
  }
  if (region === "europa-y-asia-central") {
    label = "Europa y Asia Central";
  }
  if (region === "اوروبا-واسيا-الوسطى") {
    label = "أوروبا وآسيا الوسطى";
  }
  return label;
};

const truncateText = (text) => {
  if (text.length > 65) {
    let textArray = text.split(" ");
    let textBuildup = "";
    let position = 0;
    while (textBuildup.length < 65) {
      textBuildup += textArray[position] + " ";
      position++;
    }
    if (
      textBuildup[textBuildup.length - 2] !== "?" &&
      textBuildup[textBuildup.length - 2] !== "!" &&
      textBuildup[textBuildup.length - 2] !== "."
    ) {
      text = textBuildup.slice(0, textBuildup.length - 1) + "...";
    }
  }
  return text;
};

const UpdateCard = (props) => {
  if (props.object) {
    return (
      <ThemeProvider theme={theme}>
        <Action
          to={"/" + props.object.lang + "/update/" + props.object.uid}
          cardColor={determineColor(
            props.object.data.region ? props.object.data.region.uid : "violet"
          )}
          aria-label={
            props.object.data.title[0] ? props.object.data.title[0].text : ""
          }
          // onClick={() => {
          //   document.body.style.background =
          //     theme.colors[determineColor(props.object.data.region.uid)];
          // }}
        >
          <ImageWrapper
            className="update-card__tile"
            cardColor={determineColor(
              props.object.data.region ? props.object.data.region.uid : "violet"
            )}
          >
            <OverlayImage
              imageColor={determineColor(
                props.object.data.region
                  ? props.object.data.region.uid
                  : "violet"
              )}
              imageColor="grey.2"
              backgroundImage={
                props.object.data.header_image
                  ? [
                      props.object.data.header_image.url,
                      props.object.data.header_image.half
                        ? props.object.data.header_image.half.url
                        : "",
                      props.object.data.header_image.quarter
                        ? props.object.data.header_image.quarter.url
                        : "",
                    ]
                  : undefined
              }
            />
            {/* <TagList
              style={{ position: "absolute", right: 8, left: 8, bottom: 8 }}
            >
              <Tag children="Asia" />
              <Tag children="Environment & Climate" />
              <Tag children="World Bank Group" />
              <Tag children="GMR Kamalanga Energy Limited, Thermal Power Project" />
            </TagList> */}
          </ImageWrapper>
          <Title>
            <span
              className="update-card__title"
              children={
                props.object.data.title[0]
                  ? truncateText(props.object.data.title[0].text)
                  : ""
              }
            />
          </Title>
          {/* <Text
            children={
              props.object.data.description[0]
                ? props.object.data.description[0].text.slice(0, 155)
                : ""
            }
            pb={2}
            fontSize={1}
          /> */}
          <Flex>
            <Metadata mt={"3px"} mr={3}>
              {props.object.data.publish_date
                ? moment(props.object.data.publish_date).format("MMMM DD, YYYY")
                : ""}
            </Metadata>
            <Layout>
              <TagList>
                {props.object.data.region && (
                  <Tag
                    children={determineLabel(
                      props.object.data.region
                        ? props.object.data.region.uid
                        : "violet"
                    )}
                    backgroundColor={determineColor(
                      props.object.data.region
                        ? props.object.data.region.uid
                        : "violet"
                    )}
                    color="white"
                  />
                )}
                {props.object.data.institution && (
                  <Box>
                    {props.institutions.map((institution, index) => {
                      if (
                        institution.node.uid ===
                        props.object.data.institution.uid
                      ) {
                        return (
                          <Tag
                            children={institution.node.data.title[0].text}
                            key={"institutionTag" + props.object.uid}
                          />
                        );
                      } else {
                        return null;
                      }
                    })}
                  </Box>
                )}
                {props.object.data.campaign && (
                  <Box>
                    {props.campaigns.map((campaign, index) => {
                      if (
                        campaign.node.uid === props.object.data.campaign.uid
                      ) {
                        return (
                          <Tag
                            children={campaign.node.data.title[0].text}
                            key={"campaignTag" + props.object.uid}
                          />
                        );
                      } else {
                        return null;
                      }
                    })}
                  </Box>
                )}
              </TagList>
            </Layout>
          </Flex>
        </Action>
      </ThemeProvider>
    );
  } else {
    return null;
  }
};

UpdateCard.defaultProps = {
  to: "/",
};

export default UpdateCard;



// WEBPACK FOOTER //
// ./src/components/UpdateCard.js